import { CreatePanelRequest } from 'features/drilldown'
import startCase from 'lodash/startCase'

import { ChatAction } from './gongChatApi'

const CREATE_PANELS_ACTION = 'create_panels'
const EXPLORATION_MUTATION_ACTION = 'mutate_dashboard'

const extractPanelRequests = (
  actions: ReadonlyArray<ChatAction> | undefined
): ReadonlyArray<CreatePanelRequest> => {
  if (!actions) return []
  return actions
    .filter((action) => action.type === CREATE_PANELS_ACTION)
    .flatMap((action) => (Array.isArray(action.payload) ? action.payload : []))
}

export type AssistantActionSelection =
  | { kind: 'mutation'; payload: unknown }
  | { kind: 'legacy-panels'; panels: ReadonlyArray<CreatePanelRequest> }
  | { kind: 'none' }

export const selectAssistantAction = (
  actions: ReadonlyArray<ChatAction> | undefined
): AssistantActionSelection => {
  const mutation = actions?.find(
    (action) => action.type === EXPLORATION_MUTATION_ACTION
  )
  if (mutation) return { kind: 'mutation', payload: mutation.payload }

  const panels = extractPanelRequests(actions)
  return panels.length > 0
    ? { kind: 'legacy-panels', panels }
    : { kind: 'none' }
}

/**
 * Human-readable summary of the panels a turn created, e.g.
 * "Created panels:\n * Map\n * Filter". Empty string when no panels.
 */
export const summarizePanelRequests = (
  panels: ReadonlyArray<CreatePanelRequest>
): string => {
  if (panels.length === 0) return ''
  const list = panels
    .map((panel) => `\n * ${startCase(panel.panelType)}`)
    .join('')
  return `${panels.length > 1 ? 'Created panels:' : 'Created panel:'}${list}`
}

/**
 * Build the assistant bubble from server prose and an explicitly approved
 * action summary.
 */
export const buildAssistantContent = (
  content: string,
  actionSummary?: string
): string => {
  const trimmed = content.trim()
  if (!actionSummary) return trimmed
  if (!trimmed) return actionSummary
  return `${trimmed}\n\n${actionSummary}`
}

export const buildHistoricalAssistantContent = (
  content: string,
  actions: ReadonlyArray<ChatAction> | undefined
): string => {
  const selectedAction = selectAssistantAction(actions)
  const actionSummary =
    selectedAction.kind === 'legacy-panels'
      ? summarizePanelRequests(selectedAction.panels)
      : undefined
  return buildAssistantContent(content, actionSummary)
}

export const stripMarkdown = (markdown: string): string => {
  let text = markdown

  let prev
  do {
    prev = text
    text = text.replace(/<[^>]*>/g, '')
  } while (text !== prev)

  text = text.replace(/^#{1,6}\s+/gm, '')

  text = text.replace(/\*\*([^*]+)\*\*/g, '$1')
  text = text.replace(/\*([^*]+)\*/g, '$1')
  text = text.replace(/__([^_]+)__/g, '$1')
  text = text.replace(/_([^_]+)_/g, '$1')

  text = text.replace(/```[^`]*```/g, '')
  text = text.replace(/`([^`]+)`/g, '$1')

  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  text = text.replace(/^\s*[-*+]\s+/gm, '')
  text = text.replace(/^\s*\d+\.\s+/gm, '')

  text = text.replace(/^\s*>\s+/gm, '')

  text = text.replace(/^---+$/gm, '')

  text = text.replace(/\n{3,}/g, '\n\n')
  text = text.trim()

  return text
}
